import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';

// Import authentication actions and selectors
import { verifyToken, setInitialized, didJustLogOut, dismissBiometricPrompt } from './store/slices/authSlice';
import { 
  selectIsAuthenticated, 
  selectUserRole, 
  selectIsInitialized, 
  selectIsLoading,
  selectBiometricPrompt
} from './store/slices/authSlice';

// Import critical components (loaded immediately)
import Skeleton from './components/common/Skeleton';
import LoginPage from './pages/auth/LoginPage';
import DynamicFavicon from './components/common/DynamicFavicon';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import DeleteAccountPage from './pages/legal/DeleteAccountPage';
import NativePushHandler from './components/NativePushHandler';
import WebPushHandler from './components/WebPushHandler';
import NativeAppHandler from './components/NativeAppHandler';
import UpdateAvailableBanner from './components/common/UpdateAvailableBanner';
import useVersionCheck from './hooks/useVersionCheck';
import {
  syncPendingWorkouts,
  getPendingWorkouts,
  addPendingWorkout,
  getItem as storageGetItem,
  removeItem as storageRemoveItem,
} from './utils/nativeStorage';
import { sessionAPI, hiitAPI } from './services/api';
import { FingerPrintIcon } from '@heroicons/react/24/outline';

// Key used by the workout player to autosave in-progress sessions. Kept in sync
// with WorkoutSessionSimplified.js so orphaned autosaves can be rescued on boot.
const ACTIVE_SESSION_KEY = 'activeWorkoutSession';

// Lazy load pages for code splitting
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const AdminDashboard = lazy(() => import('./pages/dashboards/AdminDashboard'));
const TrainerDashboard = lazy(() => import('./pages/dashboards/TrainerDashboard'));
const MemberDashboard = lazy(() => import('./pages/dashboards/MemberDashboard'));
const WorkoutSession = lazy(() => import('./pages/WorkoutSessionSimplified'));
const WorkoutResults = lazy(() => import('./pages/WorkoutResults'));
const ShopLanding = lazy(() => import('./pages/ShopLanding'));
const ShopBrowse = lazy(() => import('./pages/ShopBrowse'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ProductDetails = lazy(() => import('./pages/ProductDetails'));
const TrainerProfilePage = lazy(() => import('./pages/TrainerProfilePage'));
const HIITSessionPlayer = lazy(() => import('./pages/HIITSessionPlayer'));
const MemberProgramView = lazy(() => import('./pages/MemberProgramView'));
const WorkoutHistory = lazy(() => import('./pages/WorkoutHistory'));
const AllWorkoutsHistory = lazy(() => import('./pages/AllWorkoutsHistory'));
const RegistrationPaymentSuccess = lazy(() => import('./pages/RegistrationPaymentSuccess'));
const ShopPurchaseSuccess = lazy(() => import('./pages/ShopPurchaseSuccess'));
const TrainerRegistrationPaymentSuccess = lazy(() => import('./pages/TrainerRegistrationPaymentSuccess'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const TrainerGuidePage = lazy(() => import('./pages/TrainerGuidePage'));
const MemberGuidePage = lazy(() => import('./pages/MemberGuidePage'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));

// Full-page content-shaped skeleton, used for app-boot/auth-check and as the
// route-level Suspense fallback while a lazy page chunk loads. Mirrors the
// generic dashboard shape (header row + stacked section blocks) so the first
// paint roughly matches the real layout instead of a spinning circle.
const FullPageSkeleton = () => (
  <div className="min-h-screen bg-gray-50 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0" aria-hidden="true">
    {/* Header placeholder: avatar + title + action icons row */}
    <div className="bg-white shadow-sm border-b border-gray-100 native-safe-top">
      <div className="px-4 py-3 md:px-6 md:py-4 lg:max-w-7xl lg:mx-auto space-y-2">
        <div className="flex items-center space-x-3">
          <Skeleton className="h-12 w-12 flex-shrink-0" rounded="rounded-full" />
          <div className="min-w-0 space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-3.5 w-28" />
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end px-1">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-7 w-7" rounded="rounded-lg" />
          ))}
        </div>
      </div>
    </div>

    {/* Section block placeholders */}
    <div className="px-4 py-6 md:px-6 lg:max-w-7xl lg:mx-auto space-y-4">
      <Skeleton className="h-32 w-full" rounded="rounded-2xl" />
      <Skeleton className="h-48 w-full" rounded="rounded-2xl" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-40 w-full" rounded="rounded-2xl" />
        <Skeleton className="h-40 w-full" rounded="rounded-2xl" />
      </div>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);

  if (isAuthenticated) {
    switch (userRole) {
      case 'administrator':
        return <Navigate to="/admin" replace />;
      case 'trainer':
        return <Navigate to="/trainer" replace />;
      case 'member':
        return <Navigate to="/member" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return children;
};

function BiometricFirstTimePrompt() {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const prompt = useSelector(selectBiometricPrompt);
  const [saving, setSaving] = React.useState(false);

  if (!prompt) return null;

  const handleAccept = async () => {
    setSaving(true);
    try {
      const { saveBiometricCredentials, setBiometricEnabled } = await import('./utils/biometricAuth');
      const saved = await saveBiometricCredentials(prompt.email, prompt.password);
      if (saved) {
        setBiometricEnabled(true);
        const { default: toast } = await import('react-hot-toast');
        toast.success(t('biometric.enabled', { label: prompt.label, defaultValue: `${prompt.label} enabled!` }));
      }
    } catch (err) {
      console.warn('Biometric enable failed:', err);
    }
    setSaving(false);
    dispatch(dismissBiometricPrompt());
  };

  const handleDecline = () => {
    dispatch(dismissBiometricPrompt());
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-5 flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <FingerPrintIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">{prompt.label}</h3>
            <p className="text-sm text-white/80">
              {t('biometric.quickLogin', { defaultValue: 'Quick & secure login' })}
            </p>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 mb-5">
            {t('biometric.enablePrompt', {
              label: prompt.label,
              defaultValue: `Enable ${prompt.label} login for faster sign-in next time?`,
            })}
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleDecline}
              disabled={saving}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              {t('biometric.notNow', { defaultValue: 'Not now' })}
            </button>
            <button
              onClick={handleAccept}
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium transition-colors disabled:opacity-50"
            >
              {saving
                ? t('biometric.saving', { defaultValue: 'Saving...' })
                : t('biometric.enable', { defaultValue: 'Enable' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const isInitialized = useSelector(selectIsInitialized);
  const isLoading = useSelector(selectIsLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);

  // Detect a newly deployed build while this tab keeps running the old bundle.
  const { updateAvailable } = useVersionCheck();

  // Scroll to top on route change (fixes white space on back-navigation in iOS)
  useEffect(() => {
    // Scroll both window and the #root container (used in native iOS)
    window.scrollTo(0, 0);
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
  }, [location.pathname]);

  // Detect native Capacitor platform and apply native-specific styles
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('native-app');
      if (Capacitor.getPlatform() === 'ios') {
        document.body.classList.add('native-ios');
      } else if (Capacitor.getPlatform() === 'android') {
        document.body.classList.add('native-android');
      }
      // Configure status bar for iOS
      import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
        StatusBar.setStyle({ style: Style.Light }).catch(() => {});
        StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
      }).catch(() => {});

      // Remove web-only elements that cause errors in native
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) manifestLink.remove();

      // Disable Google Analytics in native (not needed, causes errors)
      const gtagScripts = document.querySelectorAll('script[src*="googletagmanager"]');
      gtagScripts.forEach(s => s.remove());
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // Check if user is already logged in on app start
      const token = localStorage.getItem('token');
      if (token) {
        // If token exists, verify it
        dispatch(verifyToken());
        return;
      }

      // No token in localStorage - try biometric auto-login on native platforms,
      // but NOT if the user just explicitly logged out (they want the login screen).
      if (Capacitor.isNativePlatform() && !didJustLogOut()) {
        try {
          const { isBiometricEnabled, hasSavedCredentials, getBiometricCredentials, clearBiometricData } = await import('./utils/biometricAuth');
          const { authAPI: bioAuthAPI } = await import('./services/api');
          if (isBiometricEnabled() && await hasSavedCredentials()) {
            const { email, password } = await getBiometricCredentials();
            try {
              const response = await bioAuthAPI.login(email, password);
              localStorage.setItem('token', response.data.token);
              dispatch(verifyToken());
              return;
            } catch (loginErr) {
              console.warn('Biometric credentials invalid, clearing:', loginErr);
              await clearBiometricData();
            }
          }
        } catch (bioErr) {
          console.warn('Biometric auto-login skipped:', bioErr);
        }
      }

      // No token and no biometric - set as initialized and not authenticated
      dispatch(setInitialized());
    };

    initAuth();
  }, [dispatch]);

  // Sync any pending workouts on app resume or reconnect (native reliability)
  useEffect(() => {
    if (!isAuthenticated) return;

    const doSync = () => {
      syncPendingWorkouts(
        (sid, data) => sessionAPI.completeSession(sid, data),
        (sid, data) => hiitAPI.updateSession(sid, data)
      ).catch(() => {});
    };

    // One-time rescue: an in-progress autosave that was orphaned (never queued,
    // not the currently open route) and is now stale (>24h) gets converted into
    // a pending-sync entry so the member's data is never silently lost.
    const rescueStaleActiveSession = async () => {
      try {
        const raw = await storageGetItem(ACTIVE_SESSION_KEY);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (!data || !data.sessionId) return;

        // Still resumable on its own route → leave it for the player to resume.
        if (window.location.pathname === `/workout/${data.sessionId}`) return;

        // Already queued elsewhere → nothing to do.
        const pending = await getPendingWorkouts();
        if (pending.some((w) => w.sessionId === data.sessionId)) return;

        // Fresh (<24h) and orphaned → still resumable, leave it in place.
        const ageMs = Date.now() - (data.savedAt || 0);
        if (ageMs <= 24 * 60 * 60 * 1000) return;

        // Stale + orphaned → reconstruct the completion payload the queue expects
        // (mirrors sessionAPI.completeSession's { globalStopwatchDuration, exercises }).
        const completedSets = data.completedSets || {};
        const base = data.savedAt || Date.now();
        const workoutData = {
          globalStopwatchDuration: Math.max(
            0,
            Math.floor((base - (data.workoutStartTime || base)) / 1000)
          ),
          finishedEarly: true,
          exercises: Object.entries(completedSets).map(([exerciseId, sets]) => ({
            exerciseId,
            sets: (sets || []).map((set) => ({
              setNumber: set.setNumber,
              ...(set.repsCompleted !== undefined && { repsCompleted: set.repsCompleted }),
              ...(set.weightUsedKg !== undefined && { weightUsedKg: set.weightUsedKg }),
              ...(set.timeSeconds !== undefined && { timeSeconds: set.timeSeconds }),
            })),
          })),
        };

        if (workoutData.exercises.length === 0) {
          // Nothing worth saving — clear the orphaned autosave.
          await storageRemoveItem(ACTIVE_SESSION_KEY);
          return;
        }

        await addPendingWorkout(data.sessionId, workoutData);
        await storageRemoveItem(ACTIVE_SESSION_KEY);
      } catch (_) {
        // Best-effort rescue — never block boot on it.
      }
    };

    rescueStaleActiveSession().finally(doSync);

    const onVisible = () => {
      if (document.visibilityState === 'visible') doSync();
    };
    const onOnline = () => doSync();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [isAuthenticated]);

  // Show a content-shaped skeleton while checking authentication (app boot)
  if (!isInitialized || isLoading) {
    return <FullPageSkeleton />;
  }

  return (
    <div className="App">
      <UpdateAvailableBanner updateAvailable={updateAvailable} />
      <NativePushHandler />
      <WebPushHandler />
      <NativeAppHandler />
      <DynamicFavicon />
      <BiometricFirstTimePrompt />
      <Suspense fallback={<FullPageSkeleton />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          } />
          
          {/* Registration Payment Success Route */}
          <Route path="/registration-payment-success" element={
            <ProtectedRoute allowedRoles={['member']}>
              <RegistrationPaymentSuccess />
            </ProtectedRoute>
          } />

          {/* Trainer Registration Payment Success - Public route for after Stripe payment */}
          <Route path="/trainer/registration-payment-success" element={<TrainerRegistrationPaymentSuccess />} />
          <Route path="/trainer/registration-payment-cancel" element={<Navigate to="/login" replace />} />

          {/* Member subscription checkout return URLs (Stripe success_url/cancel_url).
              These previously didn't exist — members landed on a 404 after paying. */}
          <Route path="/member/subscription/success" element={
            <ProtectedRoute allowedRoles={['member']}>
              <SubscriptionSuccess />
            </ProtectedRoute>
          } />
          <Route path="/member/subscription/cancel" element={<Navigate to="/member/dashboard" replace />} />

        {/* Public Shop Routes (accessible without login) */}
        <Route path="/shop" element={<ShopLanding />} />
        <Route path="/shop/browse" element={<ShopBrowse />} />
        <Route path="/shop/product/:productId" element={<ProductDetails />} />
        <Route path="/shop/success" element={<ShopPurchaseSuccess />} />

        {/* Public Blog Routes (accessible without login) */}
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Legal Pages (public) */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />

        {/* Dashboard routes with comprehensive layouts */}
        <Route path="/admin/*" element={
          <ProtectedRoute allowedRoles={['administrator']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />

        <Route path="/trainer/*" element={
          <ProtectedRoute allowedRoles={['trainer']}>
            <TrainerDashboard />
          </ProtectedRoute>
        } />

        <Route path="/member/*" element={
          <ProtectedRoute allowedRoles={['member']}>
            <MemberDashboard />
          </ProtectedRoute>
        } />

        {/* Workout Session Route */}
        <Route path="/workout/:sessionId" element={
          <ProtectedRoute allowedRoles={['member']}>
            <WorkoutSession />
          </ProtectedRoute>
        } />

        {/* Workout Results Route */}
        <Route path="/workout-results/:sessionId" element={
          <ProtectedRoute allowedRoles={['member']}>
            <WorkoutResults />
          </ProtectedRoute>
        } />

        {/* HIIT Session Player Routes */}
        <Route path="/hiit/:sessionId" element={
          <ProtectedRoute allowedRoles={['member']}>
            <HIITSessionPlayer />
          </ProtectedRoute>
        } />
        <Route path="/hiit/start/:workoutId" element={
          <ProtectedRoute allowedRoles={['member']}>
            <HIITSessionPlayer />
          </ProtectedRoute>
        } />

        {/* Member Program View Route */}
        <Route path="/member/program/:enrollmentId" element={
          <ProtectedRoute allowedRoles={['member']}>
            <MemberProgramView />
          </ProtectedRoute>
        } />

        {/* Workout History Routes */}
        <Route path="/member/workout-history/:planId" element={
          <ProtectedRoute allowedRoles={['member']}>
            <WorkoutHistory />
          </ProtectedRoute>
        } />
        <Route path="/member/all-workouts-history" element={
          <ProtectedRoute allowedRoles={['member']}>
            <AllWorkoutsHistory />
          </ProtectedRoute>
        } />

        {/* Trainer Profile Route (for members) */}
        <Route path="/trainer-profile/:trainerId" element={
          <ProtectedRoute allowedRoles={['member']}>
            <TrainerProfilePage />
          </ProtectedRoute>
        } />

        {/* Guide Pages */}
        <Route path="/trainer/guide" element={
          <ProtectedRoute allowedRoles={['trainer']}>
            <TrainerGuidePage />
          </ProtectedRoute>
        } />
        <Route path="/member/guide" element={
          <ProtectedRoute allowedRoles={['member']}>
            <MemberGuidePage />
          </ProtectedRoute>
        } />

        {/* Notification Center */}
        <Route path="/member/notifications" element={
          <ProtectedRoute allowedRoles={['member']}>
            <NotificationCenter />
          </ProtectedRoute>
        } />
        <Route path="/trainer/notifications" element={
          <ProtectedRoute allowedRoles={['trainer']}>
            <NotificationCenter />
          </ProtectedRoute>
        } />
        <Route path="/admin/notifications" element={
          <ProtectedRoute allowedRoles={['administrator']}>
            <NotificationCenter />
          </ProtectedRoute>
        } />

        {/* Default route redirects */}
        <Route path="/" element={
          isAuthenticated ? (
            userRole === 'administrator' ? <Navigate to="/admin" replace /> :
            userRole === 'trainer' ? <Navigate to="/trainer" replace /> :
            userRole === 'member' ? <Navigate to="/member" replace /> :
            <Navigate to="/login" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        } />

        {/* 404 and other routes */}
        <Route path="/unauthorized" element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900">403</h1>
              <p className="text-gray-600 mt-2">You don't have permission to access this page.</p>
            </div>
          </div>
        } />
        
        <Route path="*" element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900">404</h1>
              <p className="text-gray-600 mt-2">Page not found.</p>
            </div>
          </div>
        } />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;